import { Button, Modal, Row, Table, Tag, message } from "antd";
import { RenderInputProps } from "../types";
import { useFlow } from "../../../FlowEdit/useFlow";
import { ProColumns, ProTable } from "@ant-design/pro-components";
import { ResponseCode } from "../../../../../constants";
import { useEffect, useMemo, useState } from "react";
import {
  getMcpServerListService,
  McpServerData,
} from "../../../../services/template";
import HttpInput from "../../../../components/HttpInput";
import styles from "./index.less";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";
import { VariableItemType } from "@packages/flow-core/module";
import { useRequest } from "ahooks";
import classNames from "classnames";

type ParamDataType = {
  serverId: string;
  serverName: string;
  param: Record<string, any>;
};

type McpItemType = {
  id: string;
  name: string;
  params: string;
};

export default function SelectedMcp({
  item,
  moduleId,
  inputs,
}: RenderInputProps) {
  const [open, setOpen] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const { updateNodeValue, getAllNodes, systemVariables = [] } = useFlow();
  const [selectedMcps, setSelectedMcps] = useState<McpItemType[]>([]);

  const cols: ProColumns<McpServerData>[] = [
    {
      title: "服务器名称",
      dataIndex: "name",
      width: 200,
    },
    {
      title: "服务器描述",
      dataIndex: "description",
      hideInSearch: true,
      width: 200,
    },
  ];

  const variables = useMemo(() => {
    const templteVariables =
      getAllNodes()
        .find((node) => node.data.flowType === FlowNodeTypeEnum.templateParam)
        ?.data?.inputs.map((item) => ({
          key: item.key,
          label: item.label,
          isTemplateVariable: true,
        })) || [];
    const globaVariablelModule: Pick<VariableItemType, "key" | "label">[] =
      getAllNodes()
        .find((node) => node.data.flowType === FlowNodeTypeEnum.globalVariable)
        ?.data?.inputs.filter((input) => input.edit)
        .map((item) => ({ key: item.key, label: item.label })) || [];
    const moduleVariables =
      inputs
        ?.filter((input) => input.edit || input.toolDescription)
        .map((item) => ({
          key: item.key,
          label: item.description || item.label,
          self: true,
        })) || [];
    return [
      ...templteVariables,
      ...globaVariablelModule,
      ...moduleVariables,
      ...systemVariables,
    ];
  }, [getAllNodes, systemVariables, inputs]);

  const { data: mcpList } = useRequest(
    async () => {
      const rsp = await getMcpServerListService({
        ids: (item?.value as ParamDataType[])?.map((item) => item.serverId),
        size: 50,
        current: 1,
      });
      return rsp.data.records?.map((item) => ({
        ...item,
        id: item.id!.toString(),
      }));
    },
    {
      refreshDeps: [
        (item?.value as ParamDataType[])
          ?.map((item) => item.serverId)
          ?.join(","),
      ],
    }
  );

  useEffect(() => {
    setSelectedMcps(
      (item?.value as ParamDataType[])?.map((item) => {
        return {
          id: item.serverId!,
          name:
            mcpList?.find((mcp) => mcp.id === item.serverId)?.name ||
            item.serverName,
          params: Object.keys(item.param).join(","),
        };
      }) ?? []
    );
  }, [
    (item?.value as ParamDataType[])?.map((item) => item.serverId)?.join(","),
    mcpList,
  ]);

  return (
    <>
      <Row
        style={{
          marginBottom: 8,
        }}
      >
        <Button type="primary" onClick={() => setOpen(true)}>
          选择
        </Button>
      </Row>
      <Row>
        <Table<ParamDataType>
          className={classNames("pro-table", styles["mcp-table"])}
          dataSource={item?.value as ParamDataType[]}
          rowKey="serverId"
          columns={[
            {
              title: "mcp服务器",
              dataIndex: "serverId",
              width: 100,
              render(_v, data) {
                return (
                  mcpList?.find((mcp) => mcp.id === data.serverId)?.name ||
                  data?.serverName
                );
              },
            },
            {
              title: "可设置参数",
              dataIndex: "param",
              width: 100,
              render(_v, data, index) {
                const params = Object.entries(data.param);
                return (
                  <div>
                    {params.map(([key, val]) => (
                      <div key={key} className={styles["mcp-param-item"]}>
                        <div className={styles["mcp-param-item-key"]}>
                          {key}
                        </div>
                        <HttpInput
                          hasVariablePlugin
                          hasDropDownPlugin={false}
                          variables={variables}
                          placeholder="请输入"
                          value={val}
                          setDropdownValue={(v) => {
                            const newValue = [
                              ...(item!.value as ParamDataType[]),
                            ];
                            const valueItem = {
                              ...data,
                              param: {
                                ...data.param,
                                [key]: v,
                              },
                            };
                            newValue.splice(index, 1, valueItem);
                            updateNodeValue(moduleId, item!.key, newValue);
                            setUpdateTrigger((prev) => !prev);
                          }}
                          updateTrigger={updateTrigger}
                          onChange={(v) => {
                            const newValue = [
                              ...(item!.value as ParamDataType[]),
                            ];
                            const valueItem = {
                              ...data,
                              param: {
                                ...data.param,
                                [key]: v,
                              },
                            };
                            newValue.splice(index, 1, valueItem);
                            updateNodeValue(moduleId, item!.key, newValue);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                );
              },
            },
          ]}
          size="small"
          bordered
          pagination={false}
          locale={{ emptyText: "暂无数据" }}
        />
      </Row>
      <Modal
        title="选择MCP服务器"
        open={open}
        onCancel={() => {
          setSelectedMcps([]);
          setOpen(false);
        }}
        width={"80vw"}
        destroyOnClose
        okText="确认"
        cancelText="取消"
        onOk={() => {
          setOpen(false);
          const mcps = selectedMcps.map((mcp) => {
            const keysMap: Record<string, undefined> = {};
            mcp.params.split(",").forEach((key) => {
              const oldParamValue = (item?.value as ParamDataType[])?.find(
                (item) => item.serverId === mcp.id
              )?.param[key];
              if (oldParamValue) {
                keysMap[key] = oldParamValue;
              } else {
                keysMap[key] = undefined;
              }
            });
            return {
              serverId: mcp.id,
              serverName: mcp.name,
              param: keysMap,
            };
          });

          updateNodeValue(moduleId, item.key, mcps);
        }}
      >
        <ProTable<McpServerData>
          className="pro-table"
          rowKey="id"
          columns={cols}
          scroll={{
            x: 600,
          }}
          request={async ({ pageSize, current, ...param }) => {
            const rsp = await getMcpServerListService({
              size: pageSize!,
              current: current!,
              ...param,
            });
            if (rsp.code !== ResponseCode.Success) {
              message.error(rsp.msg);
            }
            return {
              current,
              total: rsp.data.total,
              success: rsp.code === ResponseCode.Success,
              data: rsp?.data?.records ?? [],
            };
          }}
          rowSelection={{
            onChange: (_, selectedRows) => {
              setSelectedMcps(
                selectedRows?.map((mcp) => ({
                  id: mcp.id!,
                  name: mcp.name,
                  params: mcp.params,
                })) ?? []
              );
            },
            selectedRowKeys: selectedMcps.map((mcp) => mcp.id!),
          }}
          tableAlertRender={({
            selectedRowKeys,
            selectedRows,
            onCleanSelected,
          }) => {
            return (
              <>
                <Row style={{ marginBottom: 8 }}>
                  已选 {selectedRowKeys?.length} 项
                  <a style={{ marginInlineStart: 8 }} onClick={onCleanSelected}>
                    取消选择
                  </a>
                </Row>
                <Row>
                  {selectedRows?.map((mcp) => (
                    <Tag
                      key={mcp?.id}
                      style={{ marginInlineEnd: 8, marginBottom: 8 }}
                      closable
                      color="#1677ff"
                      onClose={() => {
                        setSelectedMcps((mcps) =>
                          mcps.filter((m) => m.id !== mcp.id)
                        );
                      }}
                    >
                      {mcp?.name}
                    </Tag>
                  ))}
                </Row>
              </>
            );
          }}
          tableAlertOptionRender={false}
          toolBarRender={false}
          search={{
            collapseRender: false,
            defaultCollapsed: false,
          }}
          pagination={{ showSizeChanger: true }}
        />
      </Modal>
    </>
  );
}
